import React, { useState } from "react";
import { Loader2, Lock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LiabilityCheckbox } from "@/components/LiabilityConsent";
import { usePricing, QuotaPolicy } from "@/components/PricingDetails";

// order = { kind: "plan"|"wallet", plan?, coupon?, amount, title }
export const CheckoutDialog = ({ order, onClose }) => {
  const [b, setB] = useState({ billing_name: "", billing_company: "", billing_vat: "" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const pricing = usePricing();
  const vatPct = pricing?.vat_pct || 0;
  const vatAmount = Math.round(Number(order.amount) * vatPct) / 100;
  const gross = Math.round((Number(order.amount) + vatAmount) * 100) / 100;
  const pay = async () => {
    if (!agreed) return toast.error("يجب الموافقة على إقرار حدود المسؤولية قبل الدفع");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { kind: order.kind, plan: order.plan, addon: order.addon, yearly: !!order.yearly, coupon: order.coupon, amount: order.amount, accepted_liability: true, ...b });
      window.location.assign(data.redirect_url);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setBusy(false); }
  };
  const f = (k, label, ph, extra = {}) => (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
      <Input value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} placeholder={ph} className="bg-secondary border-border" data-testid={`checkout-${k}`} {...extra} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-popover border-border max-w-md max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> إتمام الدفع</DialogTitle>
          <DialogDescription>{order.title}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-secondary p-4 space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground"><span>قيمة الخدمة (صافي)</span><span data-testid="checkout-net">{order.amount} ريال</span></div>
          <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="checkout-vat"><span>ضريبة القيمة المضافة{vatPct ? ` (${vatPct}%)` : ""}</span><span>{vatPct ? `${vatAmount.toFixed(2)} ريال` : "0 ريال (غير مطبّقة حاليًا)"}</span></div>
          <div className="flex items-center justify-between border-t border-border pt-2">
            <span className="text-sm font-bold">المبلغ النهائي المستحق</span>
            <span className="text-2xl font-extrabold text-primary" data-testid="checkout-amount">{Number.isInteger(gross) ? gross : gross.toFixed(2)} <span className="text-sm">ريال</span></span>
          </div>
        </div>
        <QuotaPolicy pricing={pricing} only={order.kind === "plan" ? ["subscription", "credits"] : ["credits", "wallet", "refund"]} />
        <p className="text-xs text-muted-foreground">بيانات الفاتورة (اختيارية) — تصلك الفاتورة على إيميلك بعد الدفع.</p>
        {f("billing_name", "الاسم في الفاتورة", "يُستخدم اسم حسابك إن تركته فارغاً")}
        {f("billing_company", "اسم المنشأة", "اختياري")}
        {f("billing_vat", "الرقم الضريبي للمنشأة", "اختياري — 15 رقم", { dir: "ltr", inputMode: "numeric" })}
        <LiabilityCheckbox checked={agreed} onChange={setAgreed} />
        <Button onClick={pay} disabled={busy || !agreed} className="w-full gap-2 font-bold" data-testid="checkout-pay-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} ادفع بأمان عبر بوابة دفع معتمدة
        </Button>
        <p className="text-[11px] text-muted-foreground text-center leading-relaxed">تُدخل بيانات بطاقتك في صفحة بوابة الدفع المعتمدة الآمنة والمشفّرة — رواج الذكية لا تستقبل ولا تخزّن بيانات البطاقات (مدى، فيزا، ماستركارد، Apple Pay).</p>
      </DialogContent>
    </Dialog>
  );
};
